import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Briefcase } from 'lucide-react';
import FormPageShell from '../../components/Common/FormPageShell';
import PageHeader from '../../components/Common/PageHeader';
import QueryErrorBanner from '../../components/Common/QueryErrorBanner';
import { getAppHomePath } from '../../constants/navigation';
import { identityService, licenseService } from '../../services/api';
import { fetchAllPages } from '../../utils/apiPagination';
import { useTenant } from '../../contexts/TenantContext';
import { isConsultoriaTenant } from '../../utils/tenantProfile';
import { useTranslation } from 'react-i18next';

function unwrap(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
}

function daysUntil(value) {
  if (!value) return null;
  const due = new Date(String(value).slice(0, 10));
  if (Number.isNaN(due.getTime())) return null;
  return Math.round((due.getTime() - Date.now()) / 86400000);
}

function indexById(list) {
  const map = new Map();
  unwrap(list).forEach((item) => {
    if (item?.id != null) map.set(String(item.id), item);
  });
  return map;
}

function partyName(client, companiesById, personsById, namesByClientId) {
  const company = client?.company_id ? companiesById.get(String(client.company_id)) : null;
  const person = client?.person_id ? personsById.get(String(client.person_id)) : null;
  const fromLicense = namesByClientId.get(String(client?.id || ''));
  const name = [company?.name, person?.name, client?.name, client?.razao_social, fromLicense, client?.email]
    .map((value) => String(value || '').trim())
    .find((value) => value && !/^[0-9a-f]{8}-[0-9a-f-]{27,}$/i.test(value));
  return name || '—';
}

function partyDocument(client, companiesById, personsById) {
  const company = client?.company_id ? companiesById.get(String(client.company_id)) : null;
  const person = client?.person_id ? personsById.get(String(client.person_id)) : null;
  return client?.document || company?.cnpj || person?.cpf || client?.cnpj || client?.cpf || '';
}

function prazoHint(days) {
  if (days == null) return null;
  if (days < 0) return `vencido há ${Math.abs(days)} dia(s)`;
  if (days === 0) return 'vence hoje';
  return `em ${days} dia(s)`;
}

const CarteiraClientes = () => {
  const { t } = useTranslation(['nav', 'common']);
  const { tenant } = useTenant();
  const consultoria = isConsultoriaTenant(tenant);
  const party = consultoria ? 'cliente' : 'requerente';
  const parties = consultoria ? 'clientes' : 'requerentes';
  const { data: clientsRaw, isLoading: loadingClients, error: clientsError } = useQuery(
    ['carteira-clients'],
    () => fetchAllPages((params) => identityService.getClients(params)),
    { staleTime: 60_000 }
  );
  const { data: companiesRaw } = useQuery(
    ['carteira-companies'],
    () => fetchAllPages((params) => identityService.getCompanies(params)),
    { staleTime: 60_000 }
  );
  const { data: personsRaw } = useQuery(
    ['carteira-persons'],
    () => fetchAllPages((params) => identityService.getPersons(params)),
    { staleTime: 60_000 }
  );
  const { data: licensesRaw, isLoading: loadingLicenses, error: licensesError } = useQuery(
    ['carteira-licenses'],
    () => fetchAllPages((params) => licenseService.getLicencas(params)),
    { staleTime: 60_000 }
  );

  const rows = useMemo(() => {
    const clients = unwrap(clientsRaw);
    const licenses = unwrap(licensesRaw);
    const companiesById = indexById(companiesRaw);
    const personsById = indexById(personsRaw);
    const namesByClientId = new Map();
    licenses.forEach((lic) => {
      const id = String(lic.requerente_id || '');
      const nome = String(lic.requerente_nome || lic.requerente?.name || '').trim();
      if (id && nome) namesByClientId.set(id, nome);
    });
    return clients.map((client) => {
      const mine = licenses.filter((lic) => String(lic.requerente_id || '') === String(client.id || ''));
      const next = mine
        .map((lic) => lic.validade)
        .filter(Boolean)
        .sort()[0];
      const pending = mine.filter((lic) => ['1', '2', '4'].includes(String(lic.status))).length;
      return {
        id: client.id,
        name: partyName(client, companiesById, personsById, namesByClientId),
        document: partyDocument(client, companiesById, personsById),
        licenses: mine.length,
        pending,
        nextDue: next,
        days: daysUntil(next),
      };
    });
  }, [clientsRaw, companiesRaw, personsRaw, licensesRaw]);

  return (
    <FormPageShell>
      <div className="space-y-6">
        <PageHeader
          title={consultoria ? t('carteira') : 'Requerentes'}
          description={
            consultoria
              ? t('carteiraDesc')
              : 'Requerentes, licenças e prazos do órgão'
          }
          breadcrumbs={[
            { label: t('common:home'), to: getAppHomePath() },
            { label: consultoria ? t('carteira') : 'Requerentes' },
          ]}
        />
        {clientsError || licensesError ? <QueryErrorBanner error={clientsError || licensesError} /> : null}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <p className="text-xs uppercase text-gray-500">Carteira ativa</p>
            <p className="text-2xl font-semibold">{rows.length}</p>
            <p className="mt-1 text-xs text-gray-500">
              {consultoria ? 'Clientes sob gestão' : 'Requerentes cadastrados'}
            </p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <p className="text-xs uppercase text-gray-500">Licenças na carteira</p>
            <p className="text-2xl font-semibold">{rows.reduce((acc, row) => acc + row.licenses, 0)}</p>
            <p className="mt-1 text-xs text-gray-500">Somadas por {party}</p>
          </div>
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
            <p className="text-xs uppercase text-amber-800">SLA próximo ao limite</p>
            <p className="text-2xl font-semibold text-amber-900">
              {rows.filter((row) => row.days != null && row.days <= 30).length}
            </p>
            <p className="mt-1 text-xs text-amber-800">Vence em 30 dias ou já venceu</p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <p className="text-xs uppercase text-gray-500">Pendentes de homologação</p>
            <p className="text-2xl font-semibold">{rows.reduce((acc, row) => acc + row.pending, 0)}</p>
            <p className="mt-1 text-xs text-gray-500">Licenças em análise</p>
          </div>
        </div>
        <div className="data-list-card overflow-hidden">
          <div className="flex items-center gap-3 border-b border-gray-100 bg-gradient-to-r from-slate-50/80 to-white px-5 py-4">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 text-primary-800">
              <Briefcase className="h-5 w-5" aria-hidden />
            </span>
            <div>
              <p className="text-sm font-semibold text-gray-900">
                {rows.length} {rows.length === 1 ? party : parties}
              </p>
              <p className="text-xs text-gray-500">Licenças e próximo vencimento por {party}.</p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-5 py-3">{consultoria ? 'Cliente' : 'Requerente'}</th>
                  <th className="px-5 py-3">Licenças</th>
                  <th className="px-5 py-3">Pendentes</th>
                  <th className="px-5 py-3">Próximo prazo</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="border-t border-gray-100">
                    <td className="px-5 py-3">
                      <p className="font-medium text-slate-900">{row.name}</p>
                      {row.document ? <p className="text-xs text-slate-500">{row.document}</p> : null}
                    </td>
                    <td className="px-5 py-3">{row.licenses}</td>
                    <td className="px-5 py-3">{row.pending}</td>
                    <td className="px-5 py-3">
                      {row.nextDue ? String(row.nextDue).slice(0, 10) : '—'}
                      {row.days != null ? (
                        <span className={`ml-2 text-xs ${row.days < 0 ? 'text-red-700' : row.days <= 30 ? 'text-amber-700' : 'text-slate-500'}`}>
                          {prazoHint(row.days)}
                        </span>
                      ) : null}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <Link to={`/licenses?client=${encodeURIComponent(row.id)}`} className="btn btn-outline btn-sm">
                        Abrir licenças
                      </Link>
                    </td>
                  </tr>
                ))}
                {!loadingClients && !loadingLicenses && rows.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-5 py-8 text-center text-slate-500">
                      {consultoria ? 'Nenhum cliente na carteira.' : 'Nenhum requerente cadastrado.'}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </FormPageShell>
  );
};

export default CarteiraClientes;
